import { useNavigate } from "react-router-dom";
import { cn } from "@/utils/Functions";
import { PATH_ROOT } from "@/routes/paths";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { Inbox as InboxIcon, Bell, FileText } from "lucide-react";
import { useInboxData } from "@/hooks/inbox/useInboxData";
import type { InboxNotification } from "@/types/inbox.types";
import { Widget } from "./Widget";
import type { DragHandleProps } from "../../constants";
import { DocumentIcon, NotificationIcon } from "../icons";

const getPriorityIndicator = (notification: InboxNotification) => {
  if (notification.priority === "high") return "bg-red-500";
  if (notification.status === "unread") return "bg-[#111]";
  return "bg-transparent";
};

const getTypeIcon = (type: InboxNotification["type"]) => {
  switch (type) {

    case "contract":
      return <FileText className="size-4" aria-hidden="true" />;
    case "document":
      return <DocumentIcon className="size-4" aria-hidden="true" />;
    case "deal":
      return <NotificationIcon className="size-4" aria-hidden="true" />;
    default:
      return <NotificationIcon className="size-4" aria-hidden="true" />;
  }
};

export const InboxWidget = ({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) => {
  const navigate = useNavigate();
  const isDemo = useIsDemo();
  const { initialNotifications } = useInboxData();
  const recentNotifications = initialNotifications.slice(0, 4);

  return (
    <Widget
      title="Inbox"
      icon={<InboxIcon className="size-5" aria-hidden="true" />}
      linkTo={isDemo ? PATH_ROOT.demo.inbox : PATH_ROOT.main.inbox}
      dragHandleProps={dragHandleProps}
    >
      <div className="flex flex-col gap-1">
        {recentNotifications.map((notification) => (
          <button
            key={notification.id}
            onClick={() => navigate(isDemo ? PATH_ROOT.demo.inbox : PATH_ROOT.main.inbox)}
            className={cn("flex items-start gap-3 p-3 hover:bg-muted/50  text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "border-b border-border last:border-b-0 hover:bg-muted/50 hover:rounded-xl hover:border-transparent",
            )}
          >
            <div className="relative shrink-0">
              <div
                className={cn(
                  "size-8 rounded-full grid place-items-center",
                  notification.status === "unread"
                    ? "bg-muted text-muted-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {getTypeIcon(notification.type)}
              </div>
              <div
                className={cn(
                  "absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card",
                  getPriorityIndicator(notification),
                )}
                aria-label={
                  notification.priority === "high"
                    ? "High priority"
                    : notification.status === "unread"
                      ? "Unread notification"
                      : undefined
                }
              />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={cn(
                  "text-sm truncate",
                  notification.status === "unread"
                    ? "text-foreground font-medium"
                    : "text-foreground/80",
                )}
              >
                {notification.title}
              </div>
              <div className="text-muted-foreground text-xs mt-0.5 truncate">
                {notification.description}
              </div>
              <div className="text-muted-foreground/60 text-xs mt-1">{notification.relativeTime}</div>
            </div>
            {notification.actionRequired && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground dark:bg-white dark:text-neutral-800 shrink-0">
                Action required
              </span>
            )}
          </button>
        ))}
      </div>
    </Widget>
  );
};
